import React from 'react';
import { CloseButtonProps, toast } from 'react-toastify';

import ToastMessage from '../components/snackBar/ToastMessage';
import styled from 'styled-components';

import aampeTheme from '../constants/aampeTheme';
import { ToastStatus } from '../constants/toastMessage.constants';

const dismissedVersions = new Set<string>();
let shownVersion: string | null = null;

export const newVersionToastId = (version: string) => `new-version-${version}`;

/**
 * Prompts the user to reload onto a newer deployed version. Safe to call on
 * every check: it won't stack copies, it comes back if another toast cleared
 * it, and it stays away for a version the user dismissed.
 */
export function showNewVersionToast(
    version: string,
    reload: () => void = () => window.location.reload(),
): void {
    const toastId = newVersionToastId(version);
    if (dismissedVersions.has(version) || toast.isActive(toastId)) return;

    if (shownVersion && shownVersion !== version) {
        toast.dismiss(newVersionToastId(shownVersion));
    }
    shownVersion = version;

    const DismissButton = ({
        closeToast,
    }: Pick<CloseButtonProps, 'closeToast'>) => (
        <CloseButton
            type="button"
            aria-label="Dismiss"
            onClick={(event) => {
                dismissedVersions.add(version);
                closeToast(event);
            }}
        >
            ×
        </CloseButton>
    );

    toast(
        <ToastMessage
            status={ToastStatus.Info}
            title="A new version of Composer is available."
            actionButton={{ name: 'Reload', action: reload }}
        />,
        {
            toastId,
            type: ToastStatus.Info,
            position: 'top-right',
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            closeButton: DismissButton,
            // Same look as toastService.showNotificationToast, which we can't
            // call because it dismisses every toast first.
            progressStyle: {
                backgroundColor: aampeTheme.toastIconColor[ToastStatus.Info],
            },
            bodyStyle: {
                display: 'flex',
                alignItems: 'start',
            },
            style: {
                width: '360px',
                marginLeft: '-45px',
            },
        },
    );
}

export function resetNewVersionToastForTests(): void {
    dismissedVersions.clear();
    shownVersion = null;
}

const CloseButton = styled.button({
    alignSelf: 'flex-start',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.textAccessibleWG01,
});
